// ============================================================================
// math.js — allocation-free scalar helpers for the fixed-step logic.
// Nothing here allocates; the game loop calls these thousands of times/tick.
// All positions are (x, z) on the ground plane unless noted.
// ============================================================================

export const TAU = Math.PI * 2;
export const DEG = Math.PI / 180;

export function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
export function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
export function lerp(a, b, t) { return a + (b - a) * t; }
export function sign(v) { return v < 0 ? -1 : v > 0 ? 1 : 0; }

// Frame-rate independent exponential smoothing.
// `rate` is roughly "how fast to converge" (higher = snappier).
export function damp(current, target, rate, dt) {
  return lerp(current, target, 1 - Math.exp(-rate * dt));
}

export function dist2(ax, az, bx, bz) {
  const dx = ax - bx, dz = az - bz;
  return dx * dx + dz * dz;
}
export function dist(ax, az, bx, bz) {
  return Math.sqrt(dist2(ax, az, bx, bz));
}

// Shortest signed angular difference b-a wrapped to (-PI, PI].
export function angleDelta(a, b) {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  else if (d < -Math.PI) d += TAU;
  return d;
}
// Rotate angle `a` toward `b` by at most `maxStep` radians.
export function angleApproach(a, b, maxStep) {
  const d = angleDelta(a, b);
  if (d > maxStep) return a + maxStep;
  if (d < -maxStep) return a - maxStep;
  return b;
}
// Smooth angular damp.
export function angleDamp(a, b, rate, dt) {
  return a + angleDelta(a, b) * (1 - Math.exp(-rate * dt));
}

// Is point (px,pz) within an arc in front of an entity at (ox,oz) facing
// `heading`? radius = reach, halfArc = half the cone angle (radians).
export function inArc(ox, oz, heading, px, pz, radius, halfArc) {
  const dx = px - ox, dz = pz - oz;
  const d2 = dx * dx + dz * dz;
  if (d2 > radius * radius) return false;
  if (d2 < 1e-6) return true;
  const ang = Math.atan2(dx, dz); // note: +Z forward convention (see below)
  return Math.abs(angleDelta(heading, ang)) <= halfArc;
}

// Heading convention: heading 0 faces +Z. Forward vector = (sin h, cos h).
export function fwdX(h) { return Math.sin(h); }
export function fwdZ(h) { return Math.cos(h); }
export function headingTo(ox, oz, tx, tz) { return Math.atan2(tx - ox, tz - oz); }

// ---- Deterministic PRNG (mulberry32) so runs are reproducible for debug. ----
export function makeRng(seed) {
  let a = seed >>> 0;
  return function rng() {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export function rand(rng, lo, hi) { return lo + (hi - lo) * rng(); }
export function randInt(rng, lo, hi) { return (lo + Math.floor((hi - lo + 1) * rng())) | 0; }

// Smootherstep, handy for banner/anim easing.
export function smoothstep(edge0, edge1, x) {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}
export function easeOutCubic(t) { const u = 1 - t; return 1 - u * u * u; }
export function easeInCubic(t) { return t * t * t; }
